import moment from 'moment';
import config from 'config';
import isEmpty from 'lodash/isEmpty';
import isNaN from 'lodash/isNaN';
import size from 'lodash/size';
import { CSV_META, ORDER_PRIORITY } from './csv/config';
import { getVRPTimes, getVRPVehicleDefinition } from './VRPConverterTourPlanner';
import { getObjectivesLimit } from './solverConfig/config';
import { groupJobsPerTerritory, isOrderInTerritory } from './territories/TerritoriesHelpers';
import { mergeJsonReloadDurations } from './map/MarkerDragHandler';

// Convert flat reloads slice into VRP shift.reloads format
export const reloadsToVRP = (reloads) =>
  reloads.map(({ lat, lng, duration }) => ({ location: { lat, lng }, duration }));

// Convert flat parkings slice into VRP plan.shared.parking format
export const parkingsToVRP = (parkings) =>
  parkings.map(({ id, lat, lng, duration }) => ({
    id,
    places: [{ location: { lat, lng }, duration }],
  }));

// Convert pudoGroups slice into VRP plan.groups format
export const pudoGroupsToVRP = ({ groups, pudos }) =>
  groups.map((g) => ({
    id: g.id,
    pudos: pudos
      .filter((p) => p.groupId === g.id)
      .map(({ id, assignAt, lat, lng, duration, tag }) => ({
        id,
        assignAt,
        places: [{ location: { lat, lng }, duration, ...(tag != null && { tag }) }],
      })),
  }));

const { dateTimeFormat, defaults } = config;
const parseCoordinateValue = (value) =>
  typeof value === 'number' ? value : parseFloat(value.replace(',', '.'));

const parseRowTimes = (row) => {
  const knownDateFormats = [CSV_META.timeFormat];
  const timeRegexStart = /(\d?\d:\d\d)/g;
  const timeRegexEnd = /(\d?\d:\d\d)/g;
  const startTime = timeRegexStart.exec(row.StartTime)[1];
  const endTime = timeRegexEnd.exec(row.EndTime)[1];

  return [moment(startTime, knownDateFormats).format(), moment(endTime, knownDateFormats).format()];
};

const createJobPlaceFromRow = (row, territories) => {
  const place = {
    duration:
      !isNaN(parseInt(row.ServiceTime, 10)) && parseInt(row.ServiceTime, 10) >= 0
        ? parseInt(row.ServiceTime, 10) * 60
        : defaults.serviceTime,
    location: {
      lat: parseCoordinateValue(row.Latitude),
      lng: parseCoordinateValue(row.Longitude),
    },
    ...(row.Group !== undefined && { groupId: row.Group }),
    ...(row.StartTime && row.EndTime && { times: [parseRowTimes(row)] }),
    ...(row?.Territories !== undefined && { territoryIds: row.Territories }),
    ...(row.ParkingId ? { parkingIds: [row.ParkingId] } : {}),
  };

  const territory = isOrderInTerritory([row], territories);
  if (territory) place.territoryIds = [territory.name];

  return place;
};

const createTasksPerRow = (row, territories) => {
  const activityType = row.Activity;
  const places = [createJobPlaceFromRow(row, territories)];
  const demand =
    !isNaN(parseInt(row.Demand, 10)) && parseInt(row.Demand, 10) > 0
      ? [parseInt(row.Demand, 10)]
      : [defaults.demand];

  let position;
  if (row?.Position) {
    if (row.Position === 'first') {
      position = { type: 'first' };
    } else if (row.Position === 'last') {
      position = { type: 'last' };
    } else if (row.Position === 'any') {
      position = { type: 'any' };
    } else {
      position = { type: 'ordered', value: row.PositionValue || row.Position };
    }
  }

  const task = { places, demand };
  if (position) task.position = position;

  const tasks = {};
  if (activityType === 'pickup') tasks.pickups = [task];
  else tasks.deliveries = [task];

  return tasks;
};

export function mergeJobs(jobs) {
  const mergedJobs = {};

  jobs.forEach((job) => {
    const { id, tasks, skills, priority, category, maxTimeOnVehicle } = job;

    if (!mergedJobs[id]) {
      mergedJobs[id] = { id, tasks: {} };
      if (skills !== undefined) {
        mergedJobs[id].skills = skills;
      }
      if (priority !== undefined) {
        mergedJobs[id].priority = priority;
      }
      if (category !== undefined) {
        mergedJobs[id].category = category;
      }
      if (maxTimeOnVehicle !== undefined) {
        mergedJobs[id].maxTimeOnVehicle = maxTimeOnVehicle;
      }
    }

    Object.entries(tasks).forEach(([taskType, taskArray]) => {
      if (!mergedJobs[id].tasks[taskType]) {
        mergedJobs[id].tasks[taskType] = [];
      }
      mergedJobs[id].tasks[taskType].push(...taskArray);
    });
  });

  return Object.values(mergedJobs);
}
const parseNumeric = (value) => {
  if (value === null || value === undefined || value === '') return undefined;
  if (typeof value === 'number') return value;
  const num = Number(String(value).replace(',', '.'));
  return Number.isNaN(num) ? undefined : num;
};

const sanitizeCategory = (category) => {
  if (!category || typeof category !== 'string') return undefined;
  // Replace spaces and special characters with underscores to match API regex ^[a-zA-Z0-9_-]+$
  return category.replace(/[^a-zA-Z0-9_-]/g, '_');
};

export function convertParsedCSVToVRPJobs(data = [], isClustered, territories) {
  const filterPudos = data.filter((item) => item.Activity !== 'pudo');

  return filterPudos.map((row) => {
    const skillValue = Array.isArray(row.Skills) ? row.Skills[0] : row.Skills;
    const maxTimeNum = parseNumeric(row.MaxTime);
    const sanitizedCategory = row.Category ? sanitizeCategory(row.Category) : undefined;

    return {
      id: isClustered ? row.ClusterID : row.InternalID,
      ...(row.Priority ? { priority: row.Priority } : {}),
      ...(skillValue ? { skills: [skillValue] } : {}),
      ...(sanitizedCategory ? { category: sanitizedCategory } : {}),
      ...(maxTimeNum !== undefined ? { maxTimeOnVehicle: maxTimeNum } : {}),
      tasks: createTasksPerRow(row, territories),
    };
  });
}

const getDepotLocation = (depot) => {
  if (!depot) return null;
  if (depot.lat) return depot;
  if (!depot.tasks) return null;

  const firstTask = depot.tasks.deliveries || depot.tasks.pickups;
  return firstTask[0].places[0].location;
};

const jobsContainPriority = (jobs) => {
  const jobsWithPriority = jobs.filter(
    (job) => job.priority !== undefined && job.priority !== ORDER_PRIORITY.NORMAL,
  );
  return size(jobsWithPriority) > 0;
};

const getObjectiveFunction = (item) => {
  const itemObj = { type: item.key };
  if (item.threshold !== undefined) itemObj.options = { threshold: item.threshold };
  if (item.action !== undefined) itemObj.action = item.action;
  return itemObj;
};

const getObjectiveFunctions = (tourPlanner, jobs, territories) => {
  if (!tourPlanner?.solverConfiguration?.objectiveFunctions?.enabled) return null;

  const hasPrioJobs = jobsContainPriority(jobs);
  let allItems = tourPlanner.solverConfiguration.objectiveFunctions.values.filter((v) => v.enabled);
  if (!hasPrioJobs) {
    allItems = allItems.filter((item) => item.key !== 'maximizePriorityJobs');
  } else {
    const index = allItems.findIndex((item) => item.key === 'maximizePriorityJobs');
    if (index === -1) allItems.unshift({ key: 'maximizePriorityJobs', enabled: true });
  }

  if (isEmpty(territories)) {
    allItems = allItems.filter((item) => item.key !== 'maximizeTerritoryJobs');
  } else {
    const index = allItems.findIndex((item) => item.key === 'maximizeTerritoryJobs');
    if (index === -1) allItems.unshift({ key: 'maximizeTerritoryJobs', enabled: true });
  }

  const balanceObjectiveIndex = allItems.findIndex((item) => item.balance === true);
  const hasBalanceObjective = balanceObjectiveIndex !== -1;
  const balanceObjective = allItems.splice(balanceObjectiveIndex, balanceObjectiveIndex + 1)[0];

  const limit = getObjectivesLimit();
  const hasTooMany = size(allItems) > limit;
  const extraSize = size(allItems) - limit;
  const cuttingPoint = limit - extraSize - 1;
  const items = hasTooMany ? allItems.slice(0, cuttingPoint) : allItems;
  const transformed = items.map((item) => {
    if (item.key === 'minimizeCost' && hasBalanceObjective)
      return [getObjectiveFunction(item), getObjectiveFunction(balanceObjective)];
    return getObjectiveFunction(item);
  });
  if (hasTooMany) {
    const toAdd = allItems.slice(cuttingPoint);
    const extraItems = toAdd.map((item) => getObjectiveFunction(item));
    let pairs = [];
    extraItems.forEach((item, index) => {
      pairs.push(item);
      if (index % 2 === 0) {
        transformed.push(pairs);
        pairs = [];
      }
    });
  }
  return transformed;
};

export const getConfigurations = (tourPlanner, types, hasParking = false) => {
  const configuration = {};

  if (tourPlanner?.solverConfiguration?.terminationCriteria?.enabled) {
    configuration.termination = tourPlanner.solverConfiguration.terminationCriteria.values;
  }
  const experimentalFeatures = [];

  const hasMinStops = types.some((type) => type.limits?.stops?.minCount);
  if (hasMinStops) {
    experimentalFeatures.push('minStops');
  }

  const hasCharging = types.some((type) => type.charging);
  if (hasCharging) {
    experimentalFeatures.push('recharges');
  }

  if (hasParking) {
    experimentalFeatures.push('parkingIds');
  }

  if (experimentalFeatures.length > 0) {
    configuration.experimentalFeatures = experimentalFeatures;
  }

  return Object.keys(configuration).length > 0 ? configuration : null;
};

export const getFleetTypeItem = (
  index,
  vehicleAmount,
  vehicleDef,
  depotLocation,
  vrpTimes,
  tourPlanner,
  territories,
  strict,
  skills,
  currentJson,
  addVehicleLimit,
  user,
  reloads = [],
  parkings = [],
  vehicleEntry,
) => {
  const currentVehicle = vehicleEntry || tourPlanner.vehicles?.[0];
  const currentVehicleProfile =
    tourPlanner.vehicleProfiles?.find(({ name }) => name === currentVehicle?.profile) ||
    tourPlanner.vehicleProfiles?.[0];
  const { start, end } = vrpTimes;
  const mixedRestrictions = currentJson?.[0]?.shifts?.[0]?.mixingRestrictions || {};
  const idSuffixParts = [];
  if (Array.isArray(territories) && territories.length > 0) {
    idSuffixParts.push(...territories.map((t) => t.id));
  }
  if (Array.isArray(skills) && skills.length > 0) {
    idSuffixParts.push(...skills);
  }
  const idSuffix = idSuffixParts.length ? idSuffixParts.join('_') : index;

  let cleanIdSuffix = idSuffix
    ?.replace(/(?:^|_)vehicle_/g, '_')
    ?.replace(/_{2,}/g, '_')
    ?.replace(/^_+|_+$/g, '');

  const parts = cleanIdSuffix.split('_');
  const uniqueParts = [];
  parts.forEach((part) => {
    if (!uniqueParts.includes(part)) uniqueParts.push(part);
  });
  cleanIdSuffix = uniqueParts.join('_');

  const startObj = tourPlanner?.start;
  const endObj = tourPlanner?.end;

  const type = {
    ...vehicleDef,
    amount: vehicleAmount,
    id: territories || skills ? `vehicle_${cleanIdSuffix}` : vehicleDef.id,
    profile: vehicleDef.profile || 'vehicle',
    shifts: [
      {
        start: {
          ...(depotLocation && { location: depotLocation }),
          time: start.format(dateTimeFormat),
          ...(startObj || {}),
        },
      },
    ],
    ...(typeof currentVehicle?.speedFactor !== 'undefined'
      ? { speedFactor: currentVehicle.speedFactor }
      : {}),
  };

  if (tourPlanner.returnLocation.value) {
    type.shifts[0].end = {
      location: { ...tourPlanner.returnLocation.value },
      time: end.format(dateTimeFormat),
      ...(endObj || {}),
    };
  }

  const categories = user?.mapSettings?.categories;
  if (categories?.enabled && typeof categories.value === 'string') {
    const conflictingCategories = categories.value
      .split(',')
      .map((name) => name.trim())
      .filter(Boolean);

    type.shifts[0].mixingRestrictions = {
      level: 'tour',
      restrictions: [{ conflictingCategories }],
    };
  } else if (!isEmpty(mixedRestrictions) && !categories?.userChange) {
    type.shifts[0].mixingRestrictions = mixedRestrictions;
  }

  const evProfile = currentVehicleProfile?.evProfile;
  const isElectricVehicle = currentVehicleProfile?.isElectricVehicle;

  if (tourPlanner.breaks) {
    type.shifts[0].breaks = tourPlanner.breaks.map(
      ({ location, address: _a, isManual: _isManual, ...b }) => ({
        ...b,
        ...(location ? { location } : {}),
      }),
    );
  }
  if (tourPlanner.restTimes) {
    type.shifts[0].restTimes = tourPlanner.restTimes;
  }
  if (reloads.length > 0) {
    type.shifts[0].reloads = reloadsToVRP(reloads);
  }
  if (parkings.length > 0) {
    type.shifts[0].stopConfiguration = { profile: 'pedestrian' };
  }

  const limits = currentVehicle?.limits;
  if (limits) {
    type.limits = {};

    if (limits.maxDistance?.enabled) {
      type.limits.maxDistance = limits.maxDistance.value;
    }

    if (limits.shiftTime?.enabled) {
      type.limits.shiftTime = limits.shiftTime.value;
    }

    if (limits.maxDrivingTime?.enabled) {
      type.limits.maxDrivingTime = limits.maxDrivingTime.value;
    }

    if (limits.maxStops?.enabled || limits.minStops?.enabled) {
      type.limits.stops = {};

      if (limits.maxStops?.enabled) {
        type.limits.stops.maxCount = { value: limits.maxStops.value };
      }

      if (limits.minStops?.enabled) {
        type.limits.stops.minCount = { value: limits.minStops.value };
      }
    }
    if (isEmpty(type.limits) || !addVehicleLimit) {
      delete type.limits;
    }
  }

  if (Array.isArray(territories) && territories.length > 0) {
    type.territories = {
      items: territories.map((t) => ({ id: t.id })),
      strict,
    };
  }

  if (Array.isArray(skills) && skills.length > 0) {
    type.skills = skills;
  }
  if (isElectricVehicle && evProfile?.profileId) {
    const charging = evProfile.charging || {};
    const autoGeneratedInitialCharges =
      charging.initialCharge != null && charging.initialCharge !== 0
        ? Array.from({ length: vehicleAmount }, (_, i) => ({
            vehicleId: `${type.id}_${i + 1}`,
            charge: charging.initialCharge,
          }))
        : [];
    // Preserve a manually-edited initialCharges array (e.g. via the JSON viewer) instead of
    // unconditionally regenerating it from the scalar initialCharge form field, which would
    // otherwise silently discard the edit on the next VRP payload regeneration. An existing
    // array is only treated as a manual edit if it doesn't match the shape auto-generation
    // would currently produce (same length as vehicleAmount and a single uniform charge value);
    // a matching array is indistinguishable from stale auto-generated output and is regenerated
    // so that changing the initialCharge input actually takes effect.
    const existingInitialCharges = currentJson?.[0]?.charging?.initialCharges;
    const isManuallyEdited =
      Array.isArray(existingInitialCharges) &&
      existingInitialCharges.length > 0 &&
      !(
        existingInitialCharges.length === vehicleAmount &&
        new Set(existingInitialCharges.map((entry) => entry.charge)).size === 1
      );
    const initialCharges = isManuallyEdited
      ? existingInitialCharges
      : autoGeneratedInitialCharges.length
        ? autoGeneratedInitialCharges
        : existingInitialCharges || [];
    type.charging = {
      evProfileId: evProfile.profileId,
      ...(charging.minCharge != null && { minCharge: charging.minCharge }),
      ...(charging.maxCharge != null && { maxCharge: charging.maxCharge }),
      ...(charging.setupDuration != null && { setupDuration: charging.setupDuration }),
      ...(initialCharges.length && { initialCharges }),
    };
  } else if (isElectricVehicle !== false && currentJson?.[0]?.charging) {
    type.charging = currentJson[0].charging;
  }

  return type;
};

const getFleetTypeItems = (
  isImperial,
  depotLocation,
  vrpTimes,
  tourPlanner,
  territories,
  strict,
  skills,
  currentJson,
  addVehicleLimit,
  user,
  onlyShiftLocations = false,
  reloads = [],
  parkings = [],
) => {
  if (onlyShiftLocations && currentJson && Array.isArray(currentJson)) {
    return currentJson.map((type) => {
      const { recharges: _recharges, ...shiftWithoutRecharges } = type.shifts[0];
      const shift = { ...shiftWithoutRecharges };
      const endLocation = tourPlanner.returnLocation.value;
      shift.start = { ...shift.start, ...(depotLocation && { location: depotLocation }) };
      if (endLocation) {
        if (shift.end) shift.end = { ...shift.end, location: endLocation };
        else shift.end = { location: endLocation };
      }

      if (tourPlanner.breaks)
        shift.breaks = tourPlanner.breaks.map(
          ({ location: _loc, address: _a, isManual: _isManual, ...b }) => b,
        );
      else delete shift.breaks;

      if (reloads.length > 0) {
        const merged = mergeJsonReloadDurations(reloads, shift.reloads);
        shift.reloads = reloadsToVRP(merged);
      } else delete shift.reloads;

      if (parkings.length > 0) shift.stopConfiguration = { profile: 'pedestrian' };
      else delete shift.stopConfiguration;

      const shifts = [...type.shifts];
      shifts[0] = shift;
      return {
        ...type,
        shifts,
      };
    });
  }

  const terrSkillsLink = tourPlanner?.skillTerritoryAssociations || {};
  const hasTerritories = Array.isArray(territories) && territories.length > 0;
  const hasSkills = Array.isArray(skills) && skills.length > 0;
  // The FLEET_TYPES flag only gates the Step1 UI's ability to add/edit multiple vehicle
  // types; it must not truncate vehicles already present in state (e.g. from a JSON import
  // with multiple fleet types), or the VRP payload silently loses fleet types on any edit.
  const allVehicleEntries = tourPlanner.vehicles?.length
    ? tourPlanner.vehicles
    : [tourPlanner.vehicles?.[0]];
  const vehicleEntries = allVehicleEntries.filter((v) => v?.enabled !== false);
  const hasMultipleVehicles = vehicleEntries.length > 1;
  const types = [];

  const territoryMap = {};
  if (hasTerritories) {
    territories.forEach((t) => {
      if (typeof t === 'object' && t.id) {
        territoryMap[t.id] = t;
      } else if (typeof t === 'string') {
        territoryMap[t] = { id: t };
      }
    });
  }

  const usedIds = new Set();
  function ensureUniqueId(baseId) {
    let id = baseId;
    let counter = 2;
    while (usedIds.has(id)) {
      id = `${baseId}_${counter}`;
      counter++;
    }
    usedIds.add(id);
    return id;
  }

  vehicleEntries.forEach((vehicleEntry) => {
    const vehicleDef = getVRPVehicleDefinition(tourPlanner, isImperial, vehicleEntry);
    const vehicleAmount = Math.max(1, vehicleDef.amount || 2);
    const idPrefix = hasMultipleVehicles ? vehicleEntry?.id || vehicleEntry?.name : null;
    const buildVehicleId = (suffix) => (idPrefix ? `${idPrefix}_${suffix}` : suffix);

    const addedSkills = new Set();
    const addedTerritories = new Set();

    Object.entries(terrSkillsLink).forEach(([skill, linkedTerritories]) => {
      const territoryObjs = Array.isArray(linkedTerritories)
        ? linkedTerritories.map((tid) => territoryMap[tid]).filter(Boolean)
        : [];

      const vehicleId = buildVehicleId([skill, ...linkedTerritories].filter(Boolean).join('-'));

      const item = getFleetTypeItem(
        vehicleId,
        vehicleAmount,
        vehicleDef,
        depotLocation,
        vrpTimes,
        tourPlanner,
        territoryObjs.length ? territoryObjs : undefined,
        strict,
        [skill],
        currentJson,
        addVehicleLimit,
        user,
        reloads,
        parkings,
        vehicleEntry,
      );
      item.id = ensureUniqueId(item.id);
      types.push(item);

      addedSkills.add(skill);
      territoryObjs.forEach((t) => addedTerritories.add(t.id));
    });

    if (hasSkills) {
      skills.forEach((skill) => {
        if (!addedSkills.has(skill)) {
          const vehicleId = buildVehicleId(skill);
          const item = getFleetTypeItem(
            vehicleId,
            vehicleAmount,
            vehicleDef,
            depotLocation,
            vrpTimes,
            tourPlanner,
            undefined,
            strict,
            [skill],
            currentJson,
            addVehicleLimit,
            user,
            reloads,
            parkings,
            vehicleEntry,
          );
          item.id = ensureUniqueId(item.id);
          types.push(item);
        }
      });
    }

    if (hasTerritories) {
      territories.forEach((t) => {
        const tid = typeof t === 'string' ? t : t.id;
        if (!addedTerritories.has(tid)) {
          const vehicleId = buildVehicleId(tid);
          const territoryObj = territoryMap[tid];
          const item = getFleetTypeItem(
            vehicleId,
            vehicleAmount,
            vehicleDef,
            depotLocation,
            vrpTimes,
            tourPlanner,
            [territoryObj],
            strict,
            undefined,
            currentJson,
            addVehicleLimit,
            user,
            reloads,
            parkings,
            vehicleEntry,
          );
          item.id = ensureUniqueId(item.id);
          types.push(item);
        }
      });
    }

    if (!hasSkills && !hasTerritories) {
      const item = getFleetTypeItem(
        buildVehicleId('default'),
        vehicleAmount,
        vehicleDef,
        depotLocation,
        vrpTimes,
        tourPlanner,
        undefined,
        strict,
        undefined,
        currentJson,
        addVehicleLimit,
        user,
        reloads,
        parkings,
        vehicleEntry,
      );
      item.id = ensureUniqueId(item.id);
      types.push(item);
    }
  });

  return types;
};

function getVehicleProfiles(profiles, hasParking = false) {
  const mapped = profiles.map((profile) => {
    const result = {
      name: profile.name,
      type: profile.fleetType,
    };

    if (profile?.traffic) {
      result.traffic = profile.traffic;
    }
    if (profile?.avoid) {
      result.avoid = profile.avoid;
    }
    if (profile?.options) {
      result.options = profile.options;
    }
    if (profile?.exclude) {
      result.exclude = profile.exclude;
    }

    if (profile?.ignoreRouteViolations) {
      result.ignoreRouteViolations = profile.ignoreRouteViolations;
    }
    if (profile?.matrix) {
      result.matrix = profile.matrix;
    }
    if (profile?.violationMapping) {
      result.violationMapping = profile.violationMapping;
    }

    if (profile?.departureTime) {
      result.departureTime = profile.departureTime;
    }

    return result;
  });

  if (hasParking && !mapped.some((profile) => profile.name === 'pedestrian')) {
    mapped.push({ name: 'pedestrian', type: 'pedestrian' });
  }

  return mapped;
}

export function generateVRPPayload(
  jobs,
  depot,
  filename,
  tourPlanner,
  orders,
  territories,
  user,
  triggerVrp,
  jsonManual,
  isJson,
  reloads = [],
  pudoGroups = null,
  parkings = [],
  originalGroups = null,
  locationOnly = false,
) {
  const currentJson = user?.currentJson;
  const currentFleetTypes = currentJson?.fleet?.types;
  const territoriesGroups = groupJobsPerTerritory(orders, territories.areaDetails, true);
  const depotLocation = getDepotLocation(depot);
  const vrpTimes = getVRPTimes(tourPlanner);
  const fallbackGroups = pudoGroups
    ? (originalGroups || []).filter((g) => !g.pudos?.length)
    : originalGroups;
  const fallbackJsonGroups = pudoGroups
    ? (currentJson?.plan?.groups || []).filter((g) => !g.pudos?.length)
    : currentJson?.plan?.groups;
  const groups = pudoGroups?.groups?.length
    ? pudoGroupsToVRP(pudoGroups)
    : fallbackGroups?.length
    ? fallbackGroups
    : fallbackJsonGroups?.length
    ? fallbackJsonGroups
    : null;
  const parking = parkings.length ? parkingsToVRP(parkings) : null;
  const cfg = tourPlanner?.configuration;
  // When the JSON viewer has pending/edited state (isJson), always regenerate types instead of
  // reusing the cached tourPlanner.types snapshot, otherwise manual JSON edits (e.g. to
  // fleet.types[0].charging.initialCharges) would be silently discarded on the next VRP submit.
  const typesTp = !triggerVrp && !jsonManual && !isJson && tourPlanner.types;
  const allSkills = Array.from(new Set(jobs.map((job) => job.skills || []).flat()));

  const onlyShiftLocations =
    !!isJson && tourPlanner.fileType === 'json' && !jsonManual && (!triggerVrp || locationOnly);
  const types =
    typesTp ||
    getFleetTypeItems(
      user.distance === 'imperial',
      depotLocation,
      vrpTimes,
      tourPlanner,
      territoriesGroups,
      territories.strict,
      allSkills,
      currentFleetTypes,
      user?.mapSettings?.addVehicleLimit,
      user,
      onlyShiftLocations,
      reloads,
      parkings,
    );
  const enabledVehicles = (tourPlanner.vehicles || []).filter((v) => v?.enabled !== false);
  const usedProfileNames = new Set(enabledVehicles.map((v) => v.profile));
  const activeProfiles = tourPlanner.vehicleProfiles.filter((p) => usedProfileNames.has(p.name));
  // Fall back to all profiles if none matched (e.g. stale/renamed vehicle.profile reference),
  // since the solver rejects an empty fleet.profiles array.
  const profiles = getVehicleProfiles(
    activeProfiles.length > 0 ? activeProfiles : tourPlanner.vehicleProfiles,
    !!parking,
  );

  if (tourPlanner.fileType === 'json' && currentJson) {
    const fleet = {
      ...currentJson.fleet,
      types,
      profiles,
    };
    const { experimentalFeatures: originalExperimentalFeatures, ...restConfig } =
      currentJson.configuration || {};
    const cleanConfig = Object.keys(restConfig).length > 0 ? restConfig : {};
    const { experimentalFeatures: computedExperimentalFeatures = [] } =
      getConfigurations(tourPlanner, types, !!parking) || {};
    if (
      originalExperimentalFeatures?.includes('minStops') &&
      !computedExperimentalFeatures.includes('minStops') &&
      currentJson.fleet?.types?.some((t) => t.limits?.stops?.minCount)
    ) {
      computedExperimentalFeatures.push('minStops');
    }
    if (computedExperimentalFeatures.length) {
      // Preserve the original ordering of features the user already had, appending any
      // newly-detected features (and dropping ones no longer applicable) at the end.
      const orderedExisting = (originalExperimentalFeatures || []).filter((feature) =>
        computedExperimentalFeatures.includes(feature),
      );
      const newlyAdded = computedExperimentalFeatures.filter(
        (feature) => !orderedExisting.includes(feature),
      );
      cleanConfig.experimentalFeatures = [...orderedExisting, ...newlyAdded];
    }
    const cleanConfiguration = Object.keys(cleanConfig).length > 0 ? cleanConfig : undefined;
    const vrpPayload = {
      ...currentJson,
      ...(cleanConfiguration ? { configuration: cleanConfiguration } : {}),
      fleet,
      plan: { ...currentJson.plan, jobs },
    };
    if (!cleanConfiguration) delete vrpPayload.configuration;
    if (groups) {
      vrpPayload.plan = { ...currentJson.plan, groups, jobs };
    }
    if (parking) {
      vrpPayload.plan = { ...vrpPayload.plan, shared: { ...vrpPayload.plan.shared, parking } };
    } else if (vrpPayload.plan?.shared?.parking) {
      const { parking: _removed, ...restShared } = vrpPayload.plan.shared;
      vrpPayload.plan = {
        ...vrpPayload.plan,
        shared: isEmpty(restShared) ? undefined : restShared,
      };
      if (!vrpPayload.plan.shared) delete vrpPayload.plan.shared;
    }
    return vrpPayload;
  }

  const vrpPayload = {
    configuration: {},
    fleet: {
      ...(typeof tourPlanner.vehicles?.[0]?.traffic !== 'undefined' &&
      tourPlanner.vehicles?.[0]?.traffic !== 'None'
        ? { traffic: tourPlanner.vehicles[0].traffic }
        : {}),
      types,
      profiles,
    },
    plan: { jobs },
  };

  if (groups) {
    vrpPayload.plan = { groups, jobs };
  }

  if (parking) {
    vrpPayload.plan = { ...vrpPayload.plan, shared: { ...vrpPayload.plan.shared, parking } };
  }

  if (cfg) {
    vrpPayload.configuration = cfg;
  } else {
    delete vrpPayload.configuration;
  }

  const objectives = getObjectiveFunctions(tourPlanner, jobs, territories.areaDetails);

  if (objectives) {
    vrpPayload.objectives = objectives;
  }

  const configuration = getConfigurations(tourPlanner, types, !!parking);
  if (configuration) {
    vrpPayload.configuration = configuration;
  }

  return vrpPayload;
}
